/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Asset, Seconds, ConsumptionMeta, ActionRequest, Channel } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  LicensedStream,
  Selectors as PlayerSelectors,
  ActionType as PlayerActionTypes,
  PlaybackState,
} from '../../../../store/ducks/player';
import { Creators as ConsumptionEngineCreators } from './engine';

export interface Meta {
  isLive: boolean;
  asset?: Asset;
  channel?: Channel;
  stream?: LicensedStream;
  progress?: Seconds;
}

function* bypassAction(): SagaIterator<boolean> {
  return yield select(PlayerSelectors.isTrailerMode);
}

function* getMeta(): SagaIterator<ConsumptionMeta | undefined> {
  const asset: Asset | undefined = yield select(PlayerSelectors.currentAsset);
  const stream: LicensedStream | undefined = yield select(PlayerSelectors.currentStream);
  if (stream?.id) {
    const isLive: boolean = yield select(PlayerSelectors.isLive);
    const channel: Channel | undefined = yield select(PlayerSelectors.channel);
    const progress: Seconds = yield select(PlayerSelectors.currentProgress);
    const volume: number = yield select(PlayerSelectors.volume);
    const mute: boolean = yield select(PlayerSelectors.isMute);
    return {
      isLive,
      asset,
      channel,
      fileId: stream.id,
      location: progress ? Math.floor(progress) : 0,
      volume,
      mute,
    };
  }
  return undefined;
}

function* watchPlaybackState(
  requestAction: ActionRequest<PlayerActionTypes.SET_PLAYBACK_STATE, PlaybackState>
): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const { meta: state } = requestAction;

  const meta: ConsumptionMeta | undefined = yield call(getMeta);
  if (meta) {
    switch (state) {
      case PlaybackState.STREAM_LOADING: {
        yield put(ConsumptionEngineCreators.loadStart(meta));
        break;
      }
      case PlaybackState.BUFFERING: {
        yield put(ConsumptionEngineCreators.loadStart(meta));
        break;
      }
      case PlaybackState.STREAM_READY: {
        yield put(ConsumptionEngineCreators.loadEnd(meta));
        break;
      }
      case PlaybackState.PLAY: {
        yield put(ConsumptionEngineCreators.play(meta));
        break;
      }
      case PlaybackState.PLAYING: {
        yield put(ConsumptionEngineCreators.playing(meta));
        break;
      }
      case PlaybackState.PAUSED: {
        yield put(ConsumptionEngineCreators.pause(meta));
        break;
      }
      case PlaybackState.STOP: {
        yield put(ConsumptionEngineCreators.stop(meta));
        break;
      }
      default:
        break;
    }
  }
}

/*
function* watchChanneUp(): SagaIterator {
  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.zap(meta));
  }
}

function* watchChannelDown(): SagaIterator {
  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.zap(meta));
  }
}
*/

function* watchRestartRequest(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.startOver(meta));
  }
}

function* watchSeekRequest(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.scrubStart(meta));
  }
}

function* watchSeekSuccess(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.scrubEnd(meta));
  }
}

function* watchSeekFail(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.scrubEnd(meta));
  }
}

function* watchWatchRequest(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.stop(meta));
  }
}

function* watchVolume(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.volume(meta));
  }
}

function* watchMute(): SagaIterator {
  if (yield call(bypassAction)) {
    return;
  }

  const meta: ConsumptionMeta | undefined = yield call(getMeta);

  if (meta) {
    yield put(ConsumptionEngineCreators.mute(meta));
  }
}

export function* saga(): SagaIterator {
  yield all([
    takeEvery(PlayerActionTypes.SET_PLAYBACK_STATE, watchPlaybackState),
    // takeEvery(PlayerActionTypes.CHANNEL_UP, watchChanneUp),
    // takeEvery(PlayerActionTypes.CHANNEL_DOWN, watchChannelDown),
    takeLatest(PlayerActionTypes.SET_VOLUME, watchVolume),
    takeEvery(PlayerActionTypes.SET_MUTE_STATE, watchMute),
    takeEvery(PlayerActionTypes.RESTART_REQUEST, watchRestartRequest),
    takeEvery(PlayerActionTypes.SEEK_REQUEST, watchSeekRequest),
    takeEvery(PlayerActionTypes.SEEK_SUCCESS, watchSeekSuccess),
    takeEvery(PlayerActionTypes.SEEK_FAIL, watchSeekFail),
    takeEvery(PlayerActionTypes.WATCH_REQUEST, watchWatchRequest),
    // ROLLOVER
    // BITRATE_CHANGED
  ]);
}
